// Schlanker CBOR-Dekoder für das Homelab-Protokoll (docs/protokoll.md,
// „Phase 3", RFC 8949 und 8742). Ohne Svelte und ohne Abhängigkeiten, damit
// im node-Test lauffähig (lib/protokoll-cbor.test.mjs).
//
// Kann: uint, negint, bytes, text, array, map (Schlüssel Zahl oder Text),
// false/true/null/undefined und Gleitkomma 16/32/64 (das Protokoll nutzt
// keins, der Dekoder verträgt es). Lehnt ab, jeweils mit Position: Tags,
// unbestimmte Längen, andere „simple values", Ganzzahlen jenseits von
// 2^53-1, ungültiges UTF-8, abgeschnittene Daten.
//
// Nur dekodieren: Client → Server bleibt JSON.
//
// Abbildung: uint/negint → number, bytes → Uint8Array (Kopie), text →
// string, array → Array, map → Map (kein Objekt: Schlüssel wie
// „__proto__" bleiben harmlos, Zahlenschlüssel bleiben Zahlen).

/** Fehler mit Byte-Position (ab 0) im Eingabepuffer. */
export class CborFehler extends Error {
  pos: number;

  constructor(text: string, pos: number) {
    super(`CBOR: ${text} (Position ${pos})`);
    this.name = "CborFehler";
    this.pos = pos;
  }
}

/** Schachtelungstiefe, ab der abgelehnt wird (Schutz vor Stapelüberlauf). */
const MAX_TIEFE = 32;
const ZWEI_32 = 2 ** 32;
// ignoreBOM: ein führendes U+FEFF im Text bleibt erhalten (JSON behält es auch).
const utf8 = new TextDecoder("utf-8", { fatal: true, ignoreBOM: true });

class Leser {
  readonly b: Uint8Array;
  readonly v: DataView;
  p = 0;

  constructor(b: Uint8Array) {
    this.b = b;
    this.v = new DataView(b.buffer, b.byteOffset, b.byteLength);
  }

  rest(): number {
    return this.b.length - this.p;
  }

  /** Stellt sicher, dass noch `n` Bytes da sind; sonst abgeschnitten. */
  brauche(n: number): void {
    if (n > this.rest()) throw new CborFehler("abgeschnitten, " + n + " Byte erwartet, " + this.rest() + " da", this.p);
  }

  /** Das Argument zu einer zusätzlichen Information (0..27). */
  argument(ai: number, start: number): number {
    if (ai < 24) return ai;
    switch (ai) {
      case 24: {
        this.brauche(1);
        return this.b[this.p++];
      }
      case 25: {
        this.brauche(2);
        const n = this.v.getUint16(this.p);
        this.p += 2;
        return n;
      }
      case 26: {
        this.brauche(4);
        const n = this.v.getUint32(this.p);
        this.p += 4;
        return n;
      }
      case 27: {
        this.brauche(8);
        const hoch = this.v.getUint32(this.p);
        const tief = this.v.getUint32(this.p + 4);
        this.p += 8;
        // Sicher bis 2^53-1: oberes Wort höchstens 21 Bit.
        if (hoch > 0x1fffff) throw new CborFehler("Ganzzahl größer als 2^53-1", start);
        return hoch * ZWEI_32 + tief;
      }
      case 31:
        throw new CborFehler("unbestimmte Länge nicht unterstützt", start);
      default:
        throw new CborFehler("reservierte zusätzliche Information " + ai, start);
    }
  }

  /** Genau n Bytes als Ausschnitt (ohne Kopie). */
  nimm(n: number): Uint8Array {
    this.brauche(n);
    const aus = this.b.subarray(this.p, this.p + n);
    this.p += n;
    return aus;
  }

  einfach(ai: number, start: number): unknown {
    switch (ai) {
      case 20:
        return false;
      case 21:
        return true;
      case 22:
        return null;
      case 23:
        return undefined;
      case 25: {
        this.brauche(2);
        const h = this.v.getUint16(this.p);
        this.p += 2;
        return halb(h);
      }
      case 26: {
        this.brauche(4);
        const f = this.v.getFloat32(this.p);
        this.p += 4;
        return f;
      }
      case 27: {
        this.brauche(8);
        const f = this.v.getFloat64(this.p);
        this.p += 8;
        return f;
      }
      case 31:
        throw new CborFehler("unerwartetes Break (unbestimmte Länge nicht unterstützt)", start);
      default:
        throw new CborFehler("einfacher Wert " + ai + " nicht unterstützt", start);
    }
  }

  item(tiefe: number): unknown {
    const start = this.p;
    if (tiefe > MAX_TIEFE) throw new CborFehler("zu tief verschachtelt", start);
    this.brauche(1);
    const kopf = this.b[this.p++];
    const major = kopf >> 5;
    const ai = kopf & 31;
    if (major === 7) return this.einfach(ai, start);
    if (major === 6) throw new CborFehler("Tags nicht unterstützt", start);
    const n = this.argument(ai, start);
    switch (major) {
      case 0:
        return n;
      case 1:
        if (n > Number.MAX_SAFE_INTEGER - 1) throw new CborFehler("negative Ganzzahl kleiner als -(2^53-1)", start);
        return -1 - n;
      case 2:
        return this.nimm(n).slice();
      case 3: {
        const roh = this.nimm(n);
        try {
          return utf8.decode(roh);
        } catch {
          throw new CborFehler("ungültiges UTF-8", start);
        }
      }
      case 4: {
        // Jedes Element braucht mindestens ein Byte: so reserviert eine
        // erfundene Länge keinen Speicher.
        if (n > this.rest()) throw new CborFehler("Array mit " + n + " Elementen, aber nur " + this.rest() + " Byte übrig", start);
        const aus: unknown[] = [];
        for (let i = 0; i < n; i++) aus.push(this.item(tiefe + 1));
        return aus;
      }
      default: {
        // major 5: Map. Schlüssel und Wert je mindestens ein Byte.
        if (n * 2 > this.rest()) throw new CborFehler("Map mit " + n + " Einträgen, aber nur " + this.rest() + " Byte übrig", start);
        const aus = new Map<number | string, unknown>();
        for (let i = 0; i < n; i++) {
          const kpos = this.p;
          const k = this.item(tiefe + 1);
          if (typeof k !== "number" && typeof k !== "string") throw new CborFehler("Map-Schlüssel muss Zahl oder Text sein", kpos);
          aus.set(k, this.item(tiefe + 1));
        }
        return aus;
      }
    }
  }
}

/** IEEE-754-Halbgenauigkeit (RFC 8949 Anhang D). */
function halb(h: number): number {
  const vorzeichen = h & 0x8000 ? -1 : 1;
  const exp = (h >> 10) & 0x1f;
  const mant = h & 0x3ff;
  if (exp === 0) return vorzeichen * mant * 2 ** -24;
  if (exp === 31) return mant === 0 ? vorzeichen * Infinity : NaN;
  return vorzeichen * (mant + 1024) * 2 ** (exp - 25);
}

function alsBytes(daten: ArrayBuffer | Uint8Array): Uint8Array {
  return daten instanceof Uint8Array ? daten : new Uint8Array(daten);
}

/** Eine CBOR-Folge (RFC 8742): alle Items hintereinander, in Reihenfolge.
 *  Leere Eingabe → leere Liste. Bei Fehler wirft sie `CborFehler` (nichts
 *  Halbes wird geliefert). */
export function dekodiereFolge(daten: ArrayBuffer | Uint8Array): unknown[] {
  const l = new Leser(alsBytes(daten));
  const aus: unknown[] = [];
  while (l.rest() > 0) aus.push(l.item(0));
  return aus;
}

/** Wie `dekodiereFolge`, wirft aber nie: liefert die Items VOR einer
 *  kaputten Stelle und den Fehler (falls es einen gab). Für Empfänger, die
 *  das Gültige noch verarbeiten und danach die Verbindung neu aufbauen. */
export function dekodiereFolgeTeil(daten: ArrayBuffer | Uint8Array): { items: unknown[]; fehler?: CborFehler } {
  const l = new Leser(alsBytes(daten));
  const items: unknown[] = [];
  try {
    while (l.rest() > 0) items.push(l.item(0));
  } catch (e) {
    if (e instanceof CborFehler) return { items, fehler: e };
    throw e;
  }
  return { items };
}

/** Genau ein Item; Reste dahinter sind ein Fehler. */
export function dekodiere(daten: ArrayBuffer | Uint8Array): unknown {
  const l = new Leser(alsBytes(daten));
  const aus = l.item(0);
  if (l.rest() > 0) throw new CborFehler("Reste nach dem Item", l.p);
  return aus;
}
